import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

import LoadingSkeleton from "../../../components/CustomComponents/LoadingSkeleton";
import { useAppSelector } from "../../../hooks/redux";
import { maskResidentName } from "../../../util/common";
import {
  GENERAL_CHAT_PLACEHOLDERS,
  GENERAL_CHAT_STARTERS,
  PREDICT_CHAT_STARTERS,
} from "../../chatPrompts";
import ConversationMessage from "../../pages/Copilot/ConversationMessage";
import CopilotComposer from "../../pages/Copilot/CopilotComposer";
import { groupCopilotMessages } from "../../pages/Copilot/groupMessages";
import type { CopilotToolCall } from "../../pages/Copilot/types";
import { useCopilotConversation } from "../../pages/Copilot/useCopilotConversation";
import { FLAG_CHAT_SUGGESTIONS } from "../flagChatSuggestions";
import { NeoSparkIcon } from "../NeoSparkIcon";
import {
  WORKFLOW_CHAT_STARTERS,
  workflowContextExtra,
  workflowContextText,
  type WorkflowChatContext,
} from "../workflowChat";
import FlagStrip from "./FlagStrip";
import WorkflowOutputCard, { useResolvedWorkflow } from "./WorkflowOutputCard";
import styles from "./styles.module.scss";

/** Starters that fit the page the user is on. */
const startersFor = (pathname: string): string[] =>
  pathname.startsWith("/predict")
    ? [...PREDICT_CHAT_STARTERS]
    : [...GENERAL_CHAT_STARTERS];

export interface NeoChatViewMeta {
  /** Header text: the flag / workflow name, "New chat", or the chat title. */
  title: string;
}

export interface NeoChatViewProps {
  /** Remounts the conversation when the host starts or switches chats. */
  sessionKey: string;
  conversationId: number | null;
  initialPrompt?: string | null;
  onInitialPromptConsumed?: () => void;
  /** A new chat got its backend id. */
  onConversationCreated?: (conversationId: number) => void;
  /** The conversation is being created elsewhere (e.g. from a flag). */
  pendingLabel?: string | null;
  /** Chat about a workflow's latest output. */
  workflow?: WorkflowChatContext | null;
  /** Show who a flag chat is about (hosts that don't already). */
  showFlagStrip?: boolean;
  /** The host's header, given the chat's current title. */
  renderHeader?: (meta: NeoChatViewMeta) => ReactNode;
}

/**
 * ui2026: one Copilot conversation in a narrow column — the body of the
 * Ask Circadia AI panel and of the Predict flag side peek's chat. Same hook,
 * renderer and composer as the full Copilot page.
 */
const NeoChatView = ({
  sessionKey,
  conversationId,
  initialPrompt = null,
  onInitialPromptConsumed,
  onConversationCreated,
  pendingLabel = null,
  workflow: workflowInput = null,
  showFlagStrip = true,
  renderHeader,
}: NeoChatViewProps) => {
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const clinicId = useAppSelector((state) => state.clinic.userClinic?.id);
  const timeZone = useAppSelector((state) => state.clinic.userClinic?.timezone);
  const endRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  // Mirrors nearBottomRef for rendering the "scroll to latest" button.
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const setNearBottom = useCallback((near: boolean) => {
    nearBottomRef.current = near;
    setAwayFromBottom(!near);
  }, []);

  // Workflow chats: the latest run/output, then the hidden context for Neo.
  const workflow = useResolvedWorkflow(workflowInput);
  const turnContext = useMemo(
    () =>
      workflow
        ? {
            text: workflowContextText(workflow, timeZone),
            extra: workflowContextExtra(workflow),
          }
        : null,
    [workflow, timeZone]
  );

  const {
    messages,
    title,
    input,
    setInput,
    send,
    regenerate,
    retry,
    stop,
    isStreaming,
    isLoadingHistory,
    patientPanel,
  } = useCopilotConversation({
    turnContext,
    sessionKey,
    conversationId,
    initialClinicId: clinicId ? Number(clinicId) : undefined,
    initialPrompt,
    onInitialPromptConsumed,
    onConversationCreated,
  });

  const grouped = useMemo(() => groupCopilotMessages(messages), [messages]);
  const citationToolsById = useMemo(() => {
    const byId = new Map<string, CopilotToolCall[]>();
    let seen: CopilotToolCall[] = [];
    grouped.forEach((message) => {
      if (message.role !== "assistant") return;
      seen = [...seen, ...(message.tools ?? [])];
      byId.set(message.id, seen);
    });
    return byId;
  }, [grouped]);
  const finalAssistantId = [...grouped]
    .reverse()
    .find((message) => message.role === "assistant")?.id;

  // Follow the reply while the user is reading the bottom of the thread.
  useEffect(() => {
    if (nearBottomRef.current)
      endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages]);
  useEffect(() => {
    setNearBottom(true);
  }, [sessionKey, setNearBottom]);

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    setNearBottom(true);
    endRef.current?.scrollIntoView?.({ block: "end", behavior });
  };

  const flagName = patientPanel ? maskResidentName(patientPanel.name) : null;
  // Flag chats: starter questions until the user asks their own.
  const flagSuggestions =
    patientPanel &&
    !isLoadingHistory &&
    !isStreaming &&
    !grouped.some((message) => message.role === "user")
      ? FLAG_CHAT_SUGGESTIONS
      : undefined;
  // Hold the composer until the output is known, so the first message
  // always carries it.
  const workflowLoading = !!workflowInput && !workflow;
  const isNew =
    !pendingLabel &&
    !workflowInput &&
    conversationId == null &&
    grouped.length === 0;
  const showLanding = isNew && !isStreaming && !isLoadingHistory;
  const displayName =
    user?.firstName || user?.fullName?.split(/\s+/)[0] || "there";
  const headerTitle = pendingLabel
    ? `Flag: ${pendingLabel}`
    : workflowInput && grouped.length === 0
    ? workflowInput.name
    : conversationId == null && grouped.length === 0
    ? "New chat"
    : title || "Conversation";

  const thread = grouped.map((message) => (
    <ConversationMessage
      key={message.id}
      message={message}
      onSubmitForm={(text) => void send(text)}
      isFinal={message.id === finalAssistantId}
      streaming={isStreaming}
      onRegenerate={() => regenerate(message.id)}
      onRetry={() => retry(message.id)}
      citationTools={citationToolsById.get(message.id)}
    />
  ));

  const starters = (items: string[], disabled = false) => (
    <div className={styles.starters}>
      {items.map((starter) => (
        <button
          key={starter}
          type="button"
          className={styles.starter}
          disabled={disabled}
          onClick={() => void send(starter)}
        >
          <span aria-hidden>»</span>
          {starter}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.chatView}>
      {renderHeader?.({ title: headerTitle })}

      {showFlagStrip && patientPanel && (
        <FlagStrip key={sessionKey} panel={patientPanel} />
      )}

      <div
        className={styles.messages}
        onScroll={(event) => {
          const el = event.currentTarget;
          const near = el.scrollHeight - el.scrollTop - el.clientHeight < 64;
          if (near !== nearBottomRef.current) setNearBottom(near);
        }}
      >
        {pendingLabel ? (
          <div className={styles.pending} role="status">
            <span className={styles.pendingLabel}>
              <NeoSparkIcon size={14} /> Preparing {pendingLabel}’s flag
              summary…
            </span>
            <LoadingSkeleton
              ariaLabel="Preparing flag summary"
              rows={4}
              variant="lines"
            />
          </div>
        ) : isLoadingHistory ? (
          <LoadingSkeleton
            ariaLabel="Loading conversation"
            rows={4}
            variant="lines"
          />
        ) : workflowInput ? (
          <>
            <WorkflowOutputCard workflow={workflow} />
            {grouped.length === 0 && !isStreaming
              ? starters([...WORKFLOW_CHAT_STARTERS], workflowLoading)
              : thread}
          </>
        ) : showLanding ? (
          <div className={styles.landing}>
            <span className={styles.landingMark}>
              <NeoSparkIcon size={22} />
            </span>
            <h2>Hi {displayName}, how can I help?</h2>
            {starters(startersFor(location.pathname))}
          </div>
        ) : (
          thread
        )}
        <div ref={endRef} />
      </div>

      {!pendingLabel && !workflowLoading && (
        <div className={styles.composer}>
          <CopilotComposer
            value={input}
            streaming={isStreaming}
            selectedClinicId={clinicId ? Number(clinicId) : undefined}
            onChange={setInput}
            onSend={(files) => {
              scrollToBottom("auto");
              return send(undefined, files);
            }}
            onStop={stop}
            showScrollToBottom={awayFromBottom && grouped.length > 0}
            onScrollToBottom={() => scrollToBottom()}
            placeholder={
              flagName
                ? `Ask about ${flagName}…`
                : workflowInput
                ? `Ask about ${workflowInput.name}…`
                : "Ask anything…"
            }
            rotatingPlaceholders={
              flagName
                ? FLAG_CHAT_SUGGESTIONS
                : workflowInput
                ? WORKFLOW_CHAT_STARTERS
                : GENERAL_CHAT_PLACEHOLDERS
            }
            suggestions={flagSuggestions}
            onSuggestion={(text) => {
              scrollToBottom("auto");
              void send(text);
            }}
            compact
          />
        </div>
      )}
    </div>
  );
};

export default NeoChatView;
